import { UserRound, Heart, ChevronDown, ArrowRight } from 'lucide-react';
import { useEffect, useState, useMemo } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@mondivago/supabase';
import { useCountries } from '@/hooks/contents/useCountries';

import { getFlagEmoji } from '@/shared/lib/formatters';
import { AccountMenu } from '@/components/public/AccountMenu';
import { ActionModal } from '@/components/ui/Modal';
import { PreferencesModal } from '@/components/modals';
import { useSupplierProfile } from '@/hooks/suppliers';
import { SearchBar } from '@/components/search/SearchBar';
import { buildCountryPath } from '@/shared/seo/seoRouting';

import { stripAllLanguagePrefixes } from '@/i18n/routingUtils';

// ─── Public nav items ───

interface PublicNavItem {
  label: string;
  to: string;
  matchPaths?: string[];
}

const PUBLIC_NAV_ITEMS: PublicNavItem[] = [
  { label: 'Home', to: '/', matchPaths: ['/'] },
  { label: 'Search', to: '/search', matchPaths: ['/search'] },
  { label: 'Destinations', to: '/destinations', matchPaths: ['/destinations'] },
  { label: 'Explore', to: '/explore', matchPaths: ['/explore'] },
];

/** Pages that have a Hero — scroll past it to reveal the search bar */
function pageHasHero(rawPathname: string): boolean {
  // Strip locale prefix (e.g. /en-US → /, /tr-TR/search → /search)
  const pathname = stripAllLanguagePrefixes(rawPathname);
  return (
    pathname === '/' ||
    pathname.startsWith('/search') ||
    pathname.startsWith('/explore') ||
    pathname.startsWith('/tours') ||
    pathname.startsWith('/destination/') ||
    // country/city/attraction slug routes (e.g. /turkey/istanbul)
    /^\/[^/]+\/[^/]/.test(pathname)
  );
}

export function Navbar() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);
  const [isPastHero, setIsPastHero] = useState(false);

  const [isDestHovered, setIsDestHovered] = useState(false);
  const { countries, loading: countriesLoading } = useCountries(true);
  const sortedCountries = countries;

  const navigate = useNavigate();
  const location = useLocation();
  const { user, isSupplier, isTraveler, isPartner, loading, signOut, refreshSupplierStatus, refreshTravelerStatus } =
    useAuth();
  const { agency } = useSupplierProfile(); // agency = parent supplier company record

  const navBaseClass = 'text-slate-600';
  const navActiveClass = 'text-slate-950 font-bold';
  const navInactiveClass = 'hover:text-slate-950 font-medium transition-all hover:font-semibold duration-300';
  const authReady = !loading && !!user;

  // Detect when user scrolls past the Hero
  const hasHero = pageHasHero(location.pathname);
  useEffect(() => {
    // Reset on route change so nav items always show first on new pages
    setIsPastHero(false);
    if (!hasHero) return;

    // 300px clears the hero on any screen size
    const HERO_THRESHOLD = 300;

    const onScroll = (e?: Event) => {
      // The homepage wraps content in an <article> so scroll fires on that
      // element, not window. Read from the event target when available.
      const scrollTop =
        (e?.target instanceof Element ? e.target.scrollTop : null) ??
        window.scrollY ??
        document.documentElement.scrollTop;
      setIsPastHero(scrollTop > HERO_THRESHOLD);
    };

    // Check immediately in case page loaded already scrolled
    onScroll();

    // capture:true catches scroll on ANY descendant (article, div, etc.)
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    return () => {
      document.removeEventListener('scroll', onScroll, { capture: true });
    };
  }, [location.pathname, hasHero]);

  // Whether to show the search bar in place of nav links
  const showNavSearch = hasHero && isPastHero;

  // Lock body scroll when any modal open
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // ESC to close
  useEffect(() => {
    if (!isOpen) return;
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', onEscape);
    return () => {
      window.removeEventListener('keydown', onEscape);
    };
  }, [isOpen]);

  // Close on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const isNavItemActive = (to: string, matchPaths?: string[]) => {
    const path = stripAllLanguagePrefixes(location.pathname);
    const pathsToMatch = matchPaths && matchPaths.length > 0 ? matchPaths : [to];
    return pathsToMatch.some((candidate) => {
      if (candidate === '/') return path === '/';
      return path === candidate || path.startsWith(`${candidate}/`);
    });
  };

  const handleLogout = async () => {
    await signOut();
    setIsLogoutConfirmOpen(false);
    setIsOpen(false);
    void navigate('/', { replace: true });
  };

  return (
    <header className="fixed inset-x-0 top-0 z-[70] flex w-full justify-center pointer-events-none pt-[env(safe-area-inset-top)]">
      <nav className="relative px-2 lg:px-0 w-full max-w-none pointer-events-auto transition-all duration-500 ease-in-out">
        <div className="absolute inset-0 pointer-events-none rounded-none border-b border-gray-200 bg-white" />

        <div className="relative mx-auto flex max-w-6xl items-center justify-between py-2 transition-all duration-500 ease-in-out">
          <Link
            to="/"
            className="flex items-center heading-3"
            onClick={(e) => {
              if (location.pathname === '/') {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
          >
            <span className="text-slate-950">Mondi</span>
            <span className="text-lime-500">vago</span>
          </Link>

          {/* Center: animates between nav links and search bar after scrolling past hero */}
          <div className="relative hidden md:flex flex-1 items-center justify-center overflow-visible px-8">
            {/* Nav links — visible when NOT past hero */}
            <ul
              className={`absolute inset-0 flex items-center justify-center gap-10 text-sm font-medium ${navBaseClass} transition-all duration-300 ease-in-out ${
                showNavSearch
                  ? 'opacity-0 -translate-y-2 pointer-events-none'
                  : 'opacity-100 translate-y-0 pointer-events-auto'
              }`}
            >
              {PUBLIC_NAV_ITEMS.map((item) => (
                <li
                  key={item.to}
                  className="relative flex h-full items-center"
                  onMouseEnter={() => item.label === 'Destinations' && setIsDestHovered(true)}
                  onMouseLeave={() => item.label === 'Destinations' && setIsDestHovered(false)}
                >
                  <Link
                    to={item.to}
                    className={`flex items-center gap-1 h-full py-4 ${isNavItemActive(item.to, item.matchPaths) ? navActiveClass : navInactiveClass}`}
                    onClick={(e) => {
                      if (isNavItemActive(item.to, item.matchPaths)) {
                        e.preventDefault();
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}
                  >
                    {t(`nav.${item.label.toLowerCase()}`, item.label)}
                    {item.label === 'Destinations' && (
                      <ChevronDown
                        className={`h-3.5 w-3.5 transition-transform duration-300 ${isDestHovered ? 'rotate-180 text-gray-900' : 'text-slate-400'}`}
                      />
                    )}
                  </Link>

                  {/* Mega Menu Dropdown */}
                  {item.label === 'Destinations' && (
                    <div
                      className={`absolute top-full left-1/2 -translate-x-1/2 pt-2 transition-all duration-300 ease-out origin-top z-[100] ${
                        isDestHovered
                          ? 'opacity-100 translate-y-0 pointer-events-auto'
                          : 'opacity-0 -translate-y-2 pointer-events-none'
                      }`}
                    >
                      <div className="w-[780px] overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/[0.06] flex flex-col relative min-h-[360px]">
                        {countriesLoading ? (
                          <div className="w-full h-full flex flex-col items-center justify-center min-h-[360px]">
                            <span className="text-slate-400 font-medium text-sm animate-pulse">
                              Loading destinations...
                            </span>
                          </div>
                        ) : (
                          <>
                            {/* Header */}
                            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                              <div>
                                <h3 className="nav-label text-gray-900">
                                  {t('common.popularCountries', 'Popular Countries')}
                                </h3>
                                <p className="mt-0.5 desc-sm !text-gray-400">Explore our top destinations worldwide</p>
                              </div>
                            </div>

                            <div className="overflow-y-auto px-3 py-3 max-h-[340px]">
                              <p className="mb-1.5 px-2 eyebrow text-gray-400">All Destinations</p>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-1 pb-3">
                                {sortedCountries.length > 0 ? (
                                  sortedCountries.map((country) => (
                                    <Link
                                      key={country.id}
                                      to={buildCountryPath(country.slug)}
                                      className="flex items-center gap-2.5 rounded-xl px-3 py-3 text-left transition-all hover:bg-gray-50"
                                    >
                                      <span className="shrink-0 text-2xl leading-none">
                                        {country.iso2 ? getFlagEmoji(country.iso2) : '🌍'}
                                      </span>
                                      <div className="min-w-0 flex-1">
                                        <div className="nav-label text-gray-900 truncate">{country.name}</div>
                                        <div className="desc-sm !text-gray-400 truncate">
                                          {country.activity_count || 0} {t('common.tours', 'tours')}
                                        </div>
                                      </div>
                                    </Link>
                                  ))
                                ) : (
                                  <div className="col-span-full py-8 text-center text-sm text-gray-400">
                                    No countries found.
                                  </div>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>

            {/* Search bar — visible when past hero */}
            <div
              className={`w-full max-w-lg transition-all duration-300 ease-in-out ${
                showNavSearch
                  ? 'opacity-100 translate-y-0 pointer-events-auto'
                  : 'opacity-0 translate-y-2 pointer-events-none'
              }`}
            >
              <SearchBar variant="navbar" />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <PreferencesModal />
            <Link
              to="/trips/wishlist"
              title={t('nav.wishlist', 'Wishlist')}
              className={`hidden sm:flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white transition-all active:scale-95 shadow-sm ${
                location.pathname.includes('/trips/wishlist')
                  ? 'text-rose-500 bg-rose-50'
                  : 'text-slate-600 hover:bg-gray-50 hover:text-rose-500'
              }`}
            >
              <Heart
                className="h-4 w-4"
                fill={location.pathname.includes('/trips/wishlist') ? 'currentColor' : 'none'}
              />
            </Link>

            {!loading && authReady && (
              <div className="flex items-center gap-2 md:gap-3">
                <AccountMenu isSupplier={isSupplier} isPartner={isPartner} />
              </div>
            )}

            {!loading && !authReady && (
              <Link
                to="/guest/login"
                title={t('nav.signinTitle')}
                aria-label={t('nav.login')}
                className="group inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white p-1 pr-3 text-slate-950 backdrop-blur-md transition-all duration-300 hover:bg-gray-50 active:scale-95 shadow-sm"
              >
                <div className="relative h-8 w-8 shrink-0 overflow-hidden rounded-full ring-2 ring-transparent transition-all">
                  <div className="flex h-full w-full items-center justify-center bg-gray-100 text-slate-500">
                    <UserRound className="h-4 w-4" />
                  </div>
                </div>
                <span className="text-xs font-black tracking-tight">{t('nav.login')}</span>
              </Link>
            )}
          </div>
        </div>
      </nav>

      <ActionModal
        isOpen={isLogoutConfirmOpen}
        onClose={() => {
          setIsLogoutConfirmOpen(false);
        }}
        title={t('nav.logoutConfirmTitle')}
        description={t('nav.logoutConfirmDesc')}
        confirmLabel={t('nav.logout')}
        onConfirm={() => void handleLogout()}
        variant="danger"
      />
    </header>
  );
}
